import { removeBackground } from '../../../ai/ImageAI';

/**
 * Checks if an image has transparency and removes the background if needed
 * @param imageUrl The URL of the image to check and process
 * @param assetName The name of the asset (for logging)
 * @returns The processed image URL (either original or with background removed)
 */
export async function checkTransparencyAndRemoveBG(
  imageUrl: string, 
  assetName: string, 
  config: any, 
  updatePlanStatus: (newStatus: string, newStatusType?: string, copyFromUserFacing?: boolean) => void,
  writePlan: (prompt: string, newPlanStatus: string, userFacing: boolean) => void,
): Promise<string> {
  // console.log(`Checking transparency and background for asset: ${assetName}`);
  
  // Skip processing if no asset URL is available
  if (!imageUrl) {
    console.error(`No asset URL available for ${assetName}, skipping transparency check`);
    return imageUrl;
  }

  try {
    // Check if the image has transparency
    const hasTransparency = await checkTransparency(imageUrl);
    // console.log(`Transparency check for ${assetName}: ${hasTransparency}`);
    
    // If the image already has transparency, no need to remove background
    if (hasTransparency) {
      // console.log(`Asset ${assetName} already has transparency, skipping background removal`);
      return imageUrl;
    }
    
    // If no transparency, set plan status and call the remove-background function
    // console.log(`Asset ${assetName} has no transparency, removing background`);
    // updatePlanStatus("Remove Background", "Add Game Art");
    writePlan(`Update art: ${assetName}`, "Remove Background", false);
    
    const processedImageResult = await removeBackground({
      req: {
        image: imageUrl,
      },
      config
    });
    
    if (!processedImageResult) {
      console.warn(`Failed to remove background for ${assetName}, using original image`);
      return imageUrl;
    }
    
    // console.log(`Successfully removed background for ${assetName}`);
    return processedImageResult.url;
  } catch (error) {
    console.error(`Error processing transparency for ${assetName}:`, error);
    // Return the original image URL if any error occurs
    return imageUrl;
  }
}

/**
 * Checks if an image has transparency
 * @param imageUrl The URL of the image to check
 * @returns True if the image has transparency, false otherwise
 */
async function checkTransparency(imageUrl: string): Promise<boolean> {
  try {
    // Create a new image element
    const img = new Image();
    
    // Convert the image to a data URL if it's not already
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    // Check image format based on MIME type
    const imageFormat = blob.type.split('/')[1]?.toLowerCase();
    
    // PNG and WebP support transparency, JPEG does not
    if (imageFormat === 'jpeg' || imageFormat === 'jpg') {
      // console.log('Image is JPEG format which does not support transparency');
      return false;
    }
    
    // For PNG and WebP, we need to check if there's actual transparency
    return new Promise((resolve) => {
      img.onload = () => {
        // Create a canvas to draw the image
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.warn('Could not create canvas context, assuming no transparency');
          resolve(false);
          return;
        }
        
        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0);
        
        // Get the image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Check if any pixel has an alpha value less than or equal to the defined threshold
        const alphaValue = 0; // Fully transparent (255 would be fully opaque, 0 would be fully transparent)
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] <= alphaValue) {
            resolve(true);
            return;
          }
        }
        
        resolve(false); // No transparent pixels found
      };
      
      img.onerror = () => {
        console.warn('Error loading image for transparency check, assuming transparent already');
        resolve(true);
      };
      
      // Set the image source to the data URL
      img.src = URL.createObjectURL(blob);
    });
  } catch (error) {
    console.error('Error checking image transparency:', error);
    return false;
  }
}